import { UIEvent, useCallback, useEffect, useRef, useState } from "react";
import { CustomFormItem } from "../types/form";
import PopupWrapper from "../components/PopupWrapper";
import { getAllCustomForms } from "../utils/requests";
import { Loading } from "mdi-material-ui";
import { setGlobalSortPopupElement } from "../features/popupSlice";
import { AppDispatch } from "../app/store";

export const formIdData = "data-form-id";
export const customFormType = "custom-form-section";

export const formSelectHandler = (dispatch: AppDispatch, model: any) => {
    dispatch(
        setGlobalSortPopupElement(
            <CustomFormSelect
                onSelect={(form: CustomFormItem) => {
                    const content = form.content;

                    model.components(content);
                    model.setAttributes({
                        [formIdData]: form._id,
                        class: "p-4",
                    });

                    dispatch(setGlobalSortPopupElement(null));
                }}
                onClose={() => {
                    dispatch(setGlobalSortPopupElement(null));
                }}
            />
        )
    );
};

export const FormSelectPopup = ({
    onSelect,
    onClose,
}: {
    onSelect: (res: CustomFormItem) => void;
    onClose: () => void;
}) => {
    const [form, setForms] = useState<CustomFormItem[]>([]);

    useEffect(() => {
        const fetchMenus = async () => {
            const result = await getAllCustomForms({});

            setForms(result.data);
        };
        fetchMenus();
    }, []);

    return (
        <PopupWrapper onClose={onClose}>
            <div className="p-4 w-[80vw] max-w-[400px]">
                <div>
                    <p className="text-base font-semibold">
                        Select a menu to set the menu structure
                    </p>
                </div>
                <div className="py-2 flex flex-col gap-3 overflow-y-auto max-h-[400px]">
                    {form.map((menu, index) => (
                        <div
                            key={index}
                            onClick={() => onSelect(menu)}
                            className="p-2 cursor-pointer bg-gray-50 rounded-sm shadow-md hover:bg-gray-100"
                        >
                            <p>{menu.name}</p>
                        </div>
                    ))}
                </div>

                {form.length === 0 && (
                    <div className="py-2 flex items-center justify-center">
                        {/* loading */}
                        <Loading className="w-6 h-6 animate-spin" />
                    </div>
                )}
            </div>
        </PopupWrapper>
    );
};

const CustomFormSelect = ({
    onSelect,
    onClose,
}: {
    onSelect: (res: CustomFormItem) => void;
    onClose: () => void;
}) => {
    const [forms, setForms] = useState<CustomFormItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const apiCalling = useRef(false);

    const [pagePagination, setPagePagination] = useState({
        total: 0,
        lastPage: 2,
        currentPage: 1,
    });

    const getFormsCallback = useCallback(
        (currentPage: number): Promise<CustomFormItem[]> => {
            return new Promise((resolve, reject) => {
                setLoading(true);
                apiCalling.current = true;

                getAllCustomForms({
                    currentPage: currentPage,
                    perPage: 12,
                })
                    .then((res) => {
                        setPagePagination((prev) => ({
                            ...prev,
                            total: res.page.totalIndex,
                            lastPage: res.page.totalPage,
                            currentPage: res.page.currentPage,
                        }));
                        resolve(res.data);
                    })
                    .catch((err) => {
                        reject(err);
                    })
                    .finally(() => {
                        setLoading(false);
                        apiCalling.current = false;
                    });
            });
        },
        []
    );

    useEffect(() => {
        getFormsCallback(1).then(setForms);
    }, [getFormsCallback]);

    const handlePageScroll = (e: UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;

        if (
            !apiCalling.current &&
            pagePagination.currentPage < pagePagination.lastPage &&
            target.scrollTop + target.clientHeight + 120 >= target.scrollHeight
        ) {
            getFormsCallback(pagePagination.currentPage + 1).then(
                (newForms) => {
                    setForms((prev) => [...prev, ...newForms]);
                }
            );

            const updatedPagination = {
                ...pagePagination,
                currentPage: pagePagination.currentPage + 1,
            };

            setPagePagination(updatedPagination);
        }
    };

    return (
        <PopupWrapper onClose={onClose}>
            <div className="p-4 rounded-md bg-white">
                <div className="flex items-center justify-between gap-4 border-b border-gray-300 pb-2 mb-2">
                    <p className="text-base font-semibold">
                        Select a form to set the form structure
                    </p>
                </div>
                <div
                    onScroll={handlePageScroll}
                    className="grid grid-cols-2 xl:grid-cols-3 gap-4 w-[80vw] max-w-[1400px] max-h-[80vh] overflow-y-auto"
                >
                    {!!forms?.length
                        ? forms.map((form, index) => (
                              <div
                                  key={index}
                                  className="border p-2 hover:border-blue-500 cursor-pointer overflow-hidden"
                                  onClick={() => onSelect(form)}
                              >
                                  <div className="flex gap-2 justify-between">
                                      <h1 className="text-lg font-semibold mb-2">
                                          {form.name}
                                      </h1>
                                  </div>
                                  <div
                                      className=" overflow-hidden p-2 cursor-pointer"
                                      style={{
                                          zoom: 0.4,
                                          height: "800px",
                                          overflow: "hidden",
                                      }}
                                  >
                                      {form.content && (
                                          <div
                                              className="[&>form]:!block"
                                              dangerouslySetInnerHTML={{
                                                  __html: form.content,
                                              }}
                                          />
                                      )}
                                  </div>
                              </div>
                          ))
                        : !loading && "No forms found"}
                </div>
                {loading && (
                    <div className="w-full p-2 text-center text-4xl">
                        <Loading fontSize="inherit" className="animate-spin" />
                    </div>
                )}
            </div>
        </PopupWrapper>
    );
};
